import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import { RemoveScroll } from "react-remove-scroll";

import { focusNextInElement, focusPrevInElement } from "@litres/utils";
import { SpriteIcon } from "@litres-libs/sprite-icons";
import { checkIfMobileViewport } from "@litres-libs/global-hooks";
import type { IModalOverlayProps } from "../types";
import { usePortal } from "../hooks/usePortal";

import LitresLogo from "../img/litresLogo.svg";
import modaLArrow from "../img/modal-arrow.svg";

import styles from "../styles/Modal.module.scss";
import { Link } from "../../../link";

export const ModalOverlay = ({
  isOpen,
  closeOnClickOutside = false,
  onClose,
  isMobile: defaultIsMobile,
  isFullScreenMode,
  isCloseButtonShown,
  isFadeBackground,
  customCssProperties,
  arrowCssProperties,
  children,
  e2eId = "overlay",
}: IModalOverlayProps) => {
  const modal = usePortal("modal");

  const containerRef = useRef<HTMLDivElement>(null);
  const layoutRef = useRef<HTMLDivElement>(null);

  const [isMobile, setMobile] = useState(defaultIsMobile || false);

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") onClose?.();
    if (e.key === "Tab" && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) {
        focusPrevInElement(layoutRef.current);
      } else {
        focusNextInElement(layoutRef.current);
      }
    }
  };

  const handleClick = (e: MouseEvent): void => {
    if (
      !closeOnClickOutside ||
      (layoutRef.current &&
        (e.target as HTMLElement).closest(`.${layoutRef.current.className}`))
    ) {
      return;
    }
    e.preventDefault();
    onClose?.();
  };

  function actionHandler<T extends React.MouseEvent | React.KeyboardEvent>() {
    return (e: T) => {
      if ("key" in e) {
        document.body.classList.add("focus-visible-class");
      } else {
        document.body.classList.remove("focus-visible-class");
      }
      if ("key" in e && e.key !== "Enter" && e.key !== " ") {
        return;
      }
      e.stopPropagation();
    };
  }

  const childrenWithProps = useMemo(
    () =>
      React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { isMobile } as Partial<{
            isMobile?: boolean;
          }>);
        }
        return child;
      }),
    [children, isMobile]
  );

  /** при открытом можальном окне всегда фокус находится на элементах окна */
  const focusOutHandler = (e: FocusEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      isOpen &&
      layoutRef.current &&
      !layoutRef.current.contains(e.relatedTarget as HTMLElement)
    ) {
      focusNextInElement(layoutRef.current);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;

    if (container) {
      container.addEventListener("mousedown", handleClick);
    }
    document.addEventListener("keydown", onKeyDown);

    return (): void => {
      if (container) {
        container.removeEventListener("mousedown", handleClick);
      }
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    const activeEl = document.activeElement as HTMLElement;
    if (layoutRef.current && isOpen) {
      focusNextInElement(layoutRef.current);
      window.addEventListener("focusout", focusOutHandler);
    }
    const calcPosition = (): void => {
      setTimeout(() => {
        if (checkIfMobileViewport() && typeof defaultIsMobile === "undefined") {
          setMobile(true);
        } else {
          setMobile(!!defaultIsMobile);

          if (layoutRef.current && !defaultIsMobile) {
            const layoutRect = layoutRef.current.getBoundingClientRect();
            if (layoutRect.height < window.innerHeight) {
              if (containerRef.current) {
                containerRef.current.style.alignItems = "center";
              }
            } else {
              if (containerRef.current) {
                containerRef.current.style.alignItems = "flex-start";
              }
            }
          }
        }
      }, 300);
    };
    calcPosition();
    window.addEventListener("resize", calcPosition);
    return (): void => {
      if (activeEl) {
        activeEl.focus();
      }
      window.removeEventListener("focusout", focusOutHandler);
      window.removeEventListener("resize", calcPosition);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return modal
    ? ReactDOM.createPortal(
        <RemoveScroll>
          <div
            className={cn(styles.container, {
              [styles.container_mobile]: isMobile,
              [styles.container_isFullScreenMode]: !!isFullScreenMode,
              [styles.container__fade]: isFadeBackground,
            })}
            data-testid={`modal__${e2eId}--wrapper`}
            ref={containerRef}
            onClick={actionHandler<React.MouseEvent>()}
            onKeyDown={actionHandler<React.KeyboardEvent>()}
          >
            {isFullScreenMode && (
              <div className={styles.container__header}>
                <div className={styles.container__logoIcon}>
                  <img
                    src={LitresLogo}
                    width={99}
                    height={16}
                    alt="Litres logo"
                  />
                </div>
                {isCloseButtonShown && (
                  <Link
                    href="#"
                    className={styles.container__closeIcon}
                    onClick={onClose}
                  >
                    <SpriteIcon name="close" size={24} />
                  </Link>
                )}
              </div>
            )}
            <div
              className={styles.container__inner}
              data-testid={`modal__${e2eId}--container`}
              ref={layoutRef}
              tabIndex={-1}
              style={customCssProperties}
            >
              {childrenWithProps}
            </div>
            {arrowCssProperties && (
              /** стрелка */
              <div style={arrowCssProperties}>
                <img src={modaLArrow} width={24} height={12} />
              </div>
            )}
          </div>
        </RemoveScroll>,
        modal
      )
    : null;
};
